import { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../AuthContext";
import { useTranslation } from "../i18n/LanguageContext";
import { isAdminUser } from "../config/admins";
import { resolveList, resolveStatus, EVENT_STATUS } from "../config/eventOptions";
import { getEventFallbackImage } from "../utils/eventImages";
import FallbackImage from "../components/FallbackImage";
import Avatar from "../components/Avatar";
import { formatEventDate, formatTimestampDate } from "../utils/formatDate";
import { getOrCreateChat, sendEventMessageToChat } from "../utils/chat";

// Canonical public share URL. Kept as a fixed production origin (per the
// sharing spec) rather than window.location.origin, so links generated from
// a dev/staging build still point at the real app.
const SHARE_ORIGIN = "https://partybuddy.com";

function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useContext(AuthContext);
  const { t, language } = useTranslation();

  // 'loading' | 'found' | 'not-found' | 'forbidden'
  const [viewState, setViewState] = useState("loading");
  const [event, setEvent] = useState(null);
  const [creator, setCreator] = useState(null);
  const [shareState, setShareState] = useState("idle"); // idle | copied

  // "Send to Buddy" — internal sharing through the existing chat, separate
  // from the external Share action above.
  const [showBuddyModal, setShowBuddyModal] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [buddiesLoaded, setBuddiesLoaded] = useState(false);
  const [buddySearch, setBuddySearch] = useState("");
  const [sendStatus, setSendStatus] = useState({}); // buddyId -> 'sending' | 'sent' | 'error'

  useEffect(() => {
    let cancelled = false;
    setViewState("loading");
    setEvent(null);
    setCreator(null);
    setShareState("idle");

    (async () => {
      try {
        const snap = await getDoc(doc(db, "events", eventId));
        if (cancelled) return;

        if (!snap.exists()) {
          setViewState("not-found");
          return;
        }

        const data = { id: snap.id, ...snap.data() };
        setEvent(data);
        setViewState("found");

        if (data.creatorId) {
          try {
            const creatorSnap = await getDoc(doc(db, "users", data.creatorId));
            if (!cancelled && creatorSnap.exists()) setCreator(creatorSnap.data());
          } catch {
            // Non-fatal: the event itself still renders without organizer info.
          }
        }
      } catch (err) {
        // Firestore rules deny the whole read (permission-denied) both when
        // the event genuinely doesn't exist for a non-privileged reader and
        // when it exists but this user isn't authorized to see it. Collapsing
        // both into one generic "unavailable" state is intentional — it never
        // leaks which case applies, avoiding an event-ID probing side channel.
        if (!cancelled) setViewState("forbidden");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleShare = useCallback(async () => {
    if (!event) return;
    const url = `${SHARE_ORIGIN}/event/${event.id}`;

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: event.name || "PartyBuddy", url });
        return;
      } catch {
        // User cancelled the native share sheet or it failed — fall through
        // to the clipboard fallback below instead of leaving no feedback.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 2000);
    } catch {
      // Clipboard API unavailable/denied — nothing safe left to do silently.
    }
  }, [event]);

  // `preserveScroll` tells the centralized ScrollToTop (AppRoutes.js) to
  // skip its normal scroll-to-top-on-navigate behavior here — Events.js's
  // own restore effect (requirement 11) puts the scroll exactly where the
  // user left off instead. Browser Back (as opposed to this button) can't
  // attach that state, so it briefly flashes to top before the restore
  // effect corrects it a couple of frames later — harmless, just this
  // button's path is a little smoother.
  const goBack = () => navigate("/", { state: { preserveScroll: true } });

  // Loads the current user's Buddies list the first time the modal is
  // opened (not eagerly on page load) — same friends-array pattern used in
  // Buddies.js.
  const openBuddyModal = useCallback(async () => {
    setShowBuddyModal(true);
    if (buddiesLoaded || !user) return;
    try {
      const userSnap = await getDoc(doc(db, "users", user.uid));
      const friendIds = userSnap.exists() ? userSnap.data().friends || [] : [];
      const friendDocs = await Promise.all(
        friendIds.map(async (id) => {
          const snap = await getDoc(doc(db, "users", id));
          return snap.exists() ? { id, ...snap.data() } : null;
        })
      );
      setBuddies(friendDocs.filter(Boolean));
    } catch {
      setBuddies([]);
    } finally {
      setBuddiesLoaded(true);
    }
  }, [buddiesLoaded, user]);

  const handleSendToBuddy = useCallback(
    async (buddy) => {
      if (!event || !user) return;
      setSendStatus((prev) => ({ ...prev, [buddy.id]: "sending" }));
      try {
        const chat = await getOrCreateChat(user.uid, buddy.id);
        await sendEventMessageToChat(chat.id, chat.messages || [], user.uid, event.id);
        setSendStatus((prev) => ({ ...prev, [buddy.id]: "sent" }));
      } catch {
        // Most likely denied by firestore.rules — e.g. the recipient isn't
        // actually authorized to view this event (buddies-visibility event
        // and they aren't friends with the creator), or the event stopped
        // being approved between page load and send.
        setSendStatus((prev) => ({ ...prev, [buddy.id]: "error" }));
      }
    },
    [event, user]
  );

  if (viewState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 text-sm text-slate-300">
        {t("event.loading", "Loading…")}
      </div>
    );
  }

  if (viewState === "not-found" || viewState === "forbidden") {
    const isNotFound = viewState === "not-found";
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 p-6 text-center text-white">
        <h1 className="text-2xl font-bold">
          {isNotFound
            ? t("event.notFoundTitle", "Event not found")
            : t("event.unavailableTitle", "Event unavailable")}
        </h1>
        <p className="max-w-md text-sm text-slate-300">
          {isNotFound
            ? t("event.notFoundMessage", "This event doesn't exist or has been removed.")
            : t("event.unavailableMessage", "You don't have access to this event.")}
        </p>
        <button
          type="button"
          onClick={goBack}
          className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:from-blue-500 hover:to-indigo-500"
        >
          ← {t("event.back", "Back to Events")}
        </button>
      </div>
    );
  }

  // viewState === "found"
  const genres = resolveList(event.genres, event.genre);
  const moods = resolveList(event.moods, event.mood);
  const status = resolveStatus(event);
  const isOwner = !!(user && event.creatorId === user.uid);
  const isAi = event.source === "ai_suggestion";
  // Section 7: only approved + public events may be shared — never buddies,
  // draft, rejected, or any other non-public state.
  const canShare = status === EVENT_STATUS.APPROVED && event.visibility === "public";
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  // Section 5: draft/rejected events must never be sendable via chat, even
  // to the owner's own buddies and even for admins — stricter than the
  // general read-access rule, which does let owners/admins read drafts.
  const canSendToBuddy = status === EVENT_STATUS.APPROVED;
  // Internal moderation status/AI metadata is only meaningful to the owner or
  // an admin — everyone else who can legitimately read the event only sees
  // approved/public or approved/buddies content anyway.
  const showInternalStatus = (isOwner || isAdmin) && status !== EVENT_STATUS.APPROVED;
  const showAdminInfo = isAdmin && (isAi || status !== EVENT_STATUS.APPROVED);
  const showOrganizer = creator && !isAi && !isAdminUser(creator);
  const fallbackImage = getEventFallbackImage({ id: event.id, genres, moods });
  const postedOn = formatTimestampDate(event.createdAt, language);

  // For a buddies-visibility event, only buddies who are actually authorized
  // to view it (the creator themself, or a friend of the creator) are
  // offered as recipients — public events can go to any buddy. This mirrors
  // firestore.rules (the true enforcement layer for the send itself) so the
  // UI doesn't offer sends that would just be denied.
  const eligibleBuddies = buddies.filter((buddy) => {
    if (event.visibility === "public") return true;
    return buddy.id === event.creatorId || (buddy.friends || []).includes(event.creatorId);
  });
  const filteredBuddies = buddySearch.trim()
    ? eligibleBuddies.filter((b) =>
        (b.username || "").toLowerCase().includes(buddySearch.trim().toLowerCase())
      )
    : eligibleBuddies;

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 p-4 text-sm text-white md:p-6">
      <div className="mx-auto max-w-4xl">
        <button
          type="button"
          onClick={goBack}
          className="mb-4 inline-flex items-center gap-1 rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-slate-700"
        >
          ← {t("event.back", "Back to Events")}
        </button>

        <article className="glass-card overflow-hidden rounded-3xl border border-slate-700/80 shadow-2xl">
          <FallbackImage
            src={event.imageUrl}
            fallbackSrc={fallbackImage}
            alt=""
            className="h-56 w-full object-cover sm:h-72 md:h-96"
            placeholderClassName="flex h-56 w-full items-center justify-center bg-slate-800 text-slate-500 sm:h-72 md:h-96"
            placeholderLabel={t("event.noImage", "No image")}
          />

          <div className="p-4 md:p-8">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {showInternalStatus && (
                <span className="inline-flex items-center rounded-full bg-amber-500/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                  {status}
                </span>
              )}
              {isAi && isAdmin && (
                <span className="inline-flex items-center rounded-full bg-violet-600/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                  AI
                </span>
              )}
              {genres.map((g) => (
                <span
                  key={g}
                  className="inline-flex items-center rounded-full bg-blue-600/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white"
                >
                  {g}
                </span>
              ))}
              {moods.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center rounded-full bg-fuchsia-600/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white"
                >
                  {m}
                </span>
              ))}
            </div>

            <h1 className="mb-2 break-words text-2xl font-bold text-white sm:text-3xl">{event.name}</h1>

            <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
              {event.date && <span>📅 {formatEventDate(event.date, language)}</span>}
              {event.venue && <span>📍 {event.venue}</span>}
              {event.city && <span>🏙️ {event.city}</span>}
              {event.country && <span>🌍 {event.country}</span>}
            </div>

            {event.description && (
              <p className="mb-6 whitespace-pre-line text-sm leading-6 text-slate-200">{event.description}</p>
            )}

            {showOrganizer && (
              <p className="mb-6 flex items-center gap-2 text-xs text-slate-400">
                <Avatar src={creator.photoUrl} avatarConfig={creator.avatarConfig} size={24} alt={creator.username} />
                {t("event.organizer", "Organizer")}:{" "}
                <Link
                  to={`/profile?uid=${event.creatorId}`}
                  className="text-sky-300 transition hover:text-sky-200 hover:underline"
                >
                  {creator.username}
                </Link>
              </p>
            )}

            {postedOn && (
              <p className="mb-6 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {t("event.postedOn", "Posted on")} {postedOn}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {event.ticketUrl && (
                <a
                  href={event.ticketUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:from-emerald-500 hover:to-green-500"
                >
                  🎟️ {t("ticket.buy", "Buy tickets")}
                </a>
              )}
              {event.sourceUrl && (
                <a
                  href={event.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-600 bg-slate-800/80 px-5 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-700"
                >
                  🔗 {t("event.openSource", "Open source")}
                </a>
              )}
              {canSendToBuddy && (
                <button
                  type="button"
                  onClick={openBuddyModal}
                  className="inline-flex items-center justify-center rounded-xl border border-violet-500/60 bg-violet-600/20 px-5 py-2.5 text-sm font-semibold text-violet-100 transition hover:bg-violet-600/40"
                >
                  📨 {t("event.sendToBuddy", "Send to Buddy")}
                </button>
              )}
              {canShare && (
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center justify-center rounded-xl border border-sky-500/60 bg-sky-600/20 px-5 py-2.5 text-sm font-semibold text-sky-100 transition hover:bg-sky-600/40"
                >
                  {shareState === "copied"
                    ? `✅ ${t("event.linkCopied", "Link copied!")}`
                    : canNativeShare
                    ? `🔗 ${t("event.share", "Share")}`
                    : `🔗 ${t("event.copyLink", "Copy link")}`}
                </button>
              )}
            </div>

            {showAdminInfo && (
              <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs text-amber-100">
                <p className="mb-2 font-semibold uppercase tracking-[0.2em]">
                  {t("event.adminInfo", "Admin info")}
                </p>
                <ul className="space-y-1">
                  <li>{t("event.status", "Status")}: {status}</li>
                  {typeof event.confidence === "number" && <li>{t("event.confidence", "Confidence")}: {Math.round(event.confidence * 100)}%</li>}
                  {event.scoreReason && <li>{t("event.scoreReason", "Score reason")}: {event.scoreReason}</li>}
                  {event.priorityRegion && <li>{t("event.priorityRegion", "Priority region")}: {event.priorityRegion}</li>}
                  {event.notes && <li>{t("event.notes", "Notes")}: {event.notes}</li>}
                  {event.importedBy && <li>{t("event.importedBy", "Imported by")}: {event.importedBy}</li>}
                </ul>
              </div>
            )}
          </div>
        </article>
      </div>

      {showBuddyModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowBuddyModal(false)}
        >
          <div
            className="max-h-[80vh] w-full overflow-hidden rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:max-w-sm sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-700/80 p-4">
              <h2 className="text-sm font-bold text-white">
                {t("event.sendToBuddyTitle", "Send to Buddy")}
              </h2>
              <button
                type="button"
                onClick={() => setShowBuddyModal(false)}
                className="rounded-lg px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                aria-label={t("event.close", "Close")}
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              <input
                type="text"
                value={buddySearch}
                onChange={(e) => setBuddySearch(e.target.value)}
                placeholder={t("event.searchBuddies", "Search buddies…")}
                className="mb-3 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none"
              />

              <div className="max-h-72 space-y-2 overflow-y-auto">
                {!buddiesLoaded ? (
                  <p className="py-6 text-center text-xs text-slate-400">
                    {t("event.loadingBuddies", "Loading buddies…")}
                  </p>
                ) : filteredBuddies.length === 0 ? (
                  <p className="py-6 text-center text-xs text-slate-400">
                    {t("event.noBuddiesToSend", "No buddies available to send this event to.")}
                  </p>
                ) : (
                  filteredBuddies.map((buddy) => {
                    const sendState = sendStatus[buddy.id];
                    return (
                      <div
                        key={buddy.id}
                        className="flex items-center gap-3 rounded-xl border border-slate-700/60 bg-slate-800/60 p-2.5"
                      >
                        <Avatar src={buddy.photoUrl} avatarConfig={buddy.avatarConfig} size={32} alt={buddy.username} />
                        <span className="min-w-0 flex-1 truncate text-sm text-slate-100">
                          {buddy.username}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleSendToBuddy(buddy)}
                          disabled={sendState === "sending" || sendState === "sent"}
                          className="shrink-0 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:from-blue-500 hover:to-indigo-500 disabled:opacity-60"
                        >
                          {sendState === "sent"
                            ? `✅ ${t("event.sent", "Sent")}`
                            : sendState === "sending"
                            ? t("event.sending", "Sending…")
                            : sendState === "error"
                            ? t("event.sendFailed", "Retry")
                            : t("event.send", "Send")}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetails;
